import {
  Product,
  Category,
  Video,
  SpecialOffer,
  StoreInfo,
  Review,
  SearchParams,
  ApiResponse,
  PaginatedResponse,
} from "../types";
import {
  mockProducts,
  mockCategories,
  mockVideos,
  mockSpecialOffers,
  mockStoreInfo,
  mockReviews,
} from "./mockData";
import { apiService } from "./apiService";

class DataService {
  private useAPI: boolean;
  // Shared by every caller for the visit; a failed answer is dropped so the
  // next caller asks again.
  private categoriesRequest: Promise<ApiResponse<Category[]>> | null = null;

  constructor() {
    // Use API if VITE_USE_API is set to true OR if API URL, App ID, and Team ID are all provided
    const hasApiConfig =
      import.meta.env.VITE_API_URL &&
      import.meta.env.VITE_APP_ID &&
      import.meta.env.VITE_TEAM_ID;

    this.useAPI =
      import.meta.env.VITE_USE_API === "true" ||
      (hasApiConfig && import.meta.env.VITE_USE_API !== "false");

    // Log API mode status for debugging
    if (this.useAPI) {
      console.log("📡 API Mode: Enabled", {
        apiUrl: import.meta.env.VITE_API_URL,
        appId: import.meta.env.VITE_APP_ID,
        teamId: import.meta.env.VITE_TEAM_ID,
      });
    } else {
      console.log("🎭 Mock Mode: Using local mock data");
    }
  }

  setUseAPI(useAPI: boolean) {
    this.useAPI = useAPI;
  }

  // Product methods
  async getProducts(
    params?: SearchParams
  ): Promise<PaginatedResponse<Product>> {
    if (this.useAPI) {
      // In production, throw error instead of falling back to mock data
      return await apiService.getProducts(params);
    }

    let products = [...mockProducts];

    // Apply filters
    if (params?.filters) {
      const { filters } = params;

      if (filters.category) {
        products = products.filter((p) => p.categoryId === filters.category);
      }

      if (filters.priceMin !== undefined) {
        products = products.filter((p) => p.price >= filters.priceMin!);
      }

      if (filters.priceMax !== undefined) {
        products = products.filter((p) => p.price <= filters.priceMax!);
      }

      if (filters.rating !== undefined) {
        products = products.filter((p) => p.rating >= filters.rating!);
      }

      if (filters.ageRange) {
        products = products.filter((p) =>
          p.ageRange.includes(filters.ageRange!)
        );
      }

      if (filters.inStock !== undefined) {
        products = products.filter((p) => p.inStock === filters.inStock);
      }

      if (filters.onSale !== undefined) {
        products = products.filter((p) => p.onSale === filters.onSale);
      }

      if (filters.featured !== undefined) {
        products = products.filter((p) => p.featured === filters.featured);
      }
    }

    // Apply search query
    if (params?.query) {
      const query = params.query.toLowerCase();
      products = products.filter(
        (p) =>
          p.name.toLowerCase().includes(query) ||
          p.description.toLowerCase().includes(query) ||
          p.tags.some((tag) => tag.toLowerCase().includes(query))
      );
    }

    // Apply sorting
    if (params?.sortBy) {
      products.sort((a, b) => {
        let aValue: string | number | Date = a[params.sortBy!];
        let bValue: string | number | Date = b[params.sortBy!];

        if (params.sortBy === "name") {
          aValue = (aValue as string).toLowerCase();
          bValue = (bValue as string).toLowerCase();
        }

        if (params.sortOrder === "desc") {
          return bValue > aValue ? 1 : -1;
        }
        return aValue > bValue ? 1 : -1;
      });
    }

    // Apply pagination
    const page = params?.page || 1;
    const limit = params?.limit || 12;
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedProducts = products.slice(startIndex, endIndex);

    return {
      success: true,
      data: paginatedProducts,
      pagination: {
        page,
        limit,
        total: products.length,
        totalPages: Math.ceil(products.length / limit),
      },
    };
  }

  async getProduct(id: string): Promise<ApiResponse<Product>> {
    if (this.useAPI) {
      // In production, throw error instead of falling back to mock data
      return await apiService.getProduct(id);
    }

    const product = mockProducts.find((p) => p.id === id);

    if (!product) {
      return {
        success: false,
        error: "Product not found",
      };
    }

    return {
      success: true,
      data: product,
    };
  }

  async getFeaturedProducts(): Promise<ApiResponse<Product[]>> {
    if (this.useAPI) {
      // In production, throw error instead of falling back to mock data
      return await apiService.getFeaturedProducts();
    }

    const featuredProducts = mockProducts.filter((p) => p.featured);

    return {
      success: true,
      data: featuredProducts,
    };
  }

  // Category methods
  async getCategories(): Promise<ApiResponse<Category[]>> {
    // Only use API if enabled AND device is registered
    if (this.useAPI) {
      this.categoriesRequest ??= apiService.getCategories().then((response) => {
        if (!response.success) this.categoriesRequest = null;
        return response;
      });
      return this.categoriesRequest;
    }

    return {
      success: true,
      data: mockCategories,
    };
  }

  async getCategory(id: string): Promise<ApiResponse<Category>> {
    if (this.useAPI) {
      const categoriesResponse = await apiService.getCategories();
      if (categoriesResponse.success && categoriesResponse.data) {
        const category = categoriesResponse.data.find((c) => c.id === id);
        if (category) {
          return {
            success: true,
            data: category,
          };
        }
      }
      return {
        success: false,
        error: "Category not found",
      };
    }

    const category = mockCategories.find((c) => c.id === id);

    if (!category) {
      return {
        success: false,
        error: "Category not found",
      };
    }

    return {
      success: true,
      data: category,
    };
  }

  // Video methods
  async getVideos(): Promise<ApiResponse<Video[]>> {
    if (this.useAPI) {
      // TODO: Implement API call
      throw new Error("API not implemented for getVideos");
    }

    return {
      success: true,
      data: mockVideos,
    };
  }

  async getFeaturedVideos(): Promise<ApiResponse<Video[]>> {
    if (this.useAPI) {
      // TODO: Implement API call
      throw new Error("API not implemented for getFeaturedVideos");
    }

    const featuredVideos = mockVideos.filter((v) => v.featured);

    return {
      success: true,
      data: featuredVideos,
    };
  }

  // Special offers methods
  async getSpecialOffers(): Promise<ApiResponse<SpecialOffer[]>> {
    if (this.useAPI) {
      // In production, throw error instead of falling back to mock data
      return await apiService.getSpecialOffers();
    }

    return {
      success: true,
      data: mockSpecialOffers,
    };
  }

  async getFeaturedOffers(): Promise<ApiResponse<SpecialOffer[]>> {
    if (this.useAPI) {
      // TODO: Implement API call
      throw new Error("API not implemented for getFeaturedOffers");
    }

    const featuredOffers = mockSpecialOffers.filter((o) => o.featured);

    return {
      success: true,
      data: featuredOffers,
    };
  }

  // Store info methods
  async getStoreInfo(): Promise<ApiResponse<StoreInfo>> {
    if (this.useAPI) {
      // In production, throw error instead of falling back to mock data
      return await apiService.getStoreInfo();
    }

    return {
      success: true,
      data: mockStoreInfo,
    };
  }

  // Review methods
  async getProductReviews(productId: string): Promise<ApiResponse<Review[]>> {
    if (this.useAPI) {
      // TODO: Implement API call
      throw new Error("API not implemented for getProductReviews");
    }

    const reviews = mockReviews.filter((r) => r.productId === productId);

    return {
      success: true,
      data: reviews,
    };
  }

  async addReview(
    review: Omit<Review, "id" | "createdAt">
  ): Promise<ApiResponse<Review>> {
    if (this.useAPI) {
      // TODO: Implement API call
      throw new Error("API not implemented for addReview");
    }

    const newReview: Review = {
      ...review,
      id: `review_${Date.now()}`,
      createdAt: new Date(),
    };

    // In a real app, this would be saved to the database
    mockReviews.push(newReview);

    return {
      success: true,
      data: newReview,
    };
  }

  // Newsletter methods
  async subscribeToNewsletter(
    _email: string
  ): Promise<ApiResponse<{ message: string }>> {
    if (this.useAPI) {
      // TODO: Implement API call
      throw new Error("API not implemented for subscribeToNewsletter");
    }

    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 1000));

    return {
      success: true,
      data: { message: "Successfully subscribed to newsletter!" },
    };
  }

  // Contact methods
  async sendContactMessage(_message: {
    firstName: string;
    lastName: string;
    email: string;
    subject: string;
    message: string;
  }): Promise<ApiResponse<{ message: string }>> {
    if (this.useAPI) {
      // TODO: Implement API call
      throw new Error("API not implemented for sendContactMessage");
    }

    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 1000));

    return {
      success: true,
      data: {
        message: "Thank you for your message! We'll get back to you soon.",
      },
    };
  }
}

// Export singleton instance
export const dataService = new DataService();
export default dataService;
